import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { ProofingViewState, SkittleCalloutStatus } from '../store/schema/ProofingViewState';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { PluginUIProps } from 'owa-editor/lib/store/schema/PluginWithUI';
import ProofingPlugin from '../plugin/ProofingPlugin';
import { DialogPremiumPreviewSkittle } from './DialogPremiumPreviewSkittle';
import { logOnSkittleClicked } from '../utils/telemetry/logOnSkittleClicked';
import { NUMBER_OF_DAYS_FREE_TRIAL, NUMBER_OF_SUGGESTIONS } from '../constants';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { getAriaLabelForSkittle } from '../utils/getAriaLabelForSkittle';
import { onUpdateSkittleCallout, onUpdatePermiumPreviewSource } from '../mutators';
import { UpsellSkittleCallout } from '../components/UpsellSkittleCallout';
import { UpsellPremiumPreviewBase } from '../components/UpsellPremiumPreviewBase';
import { PremiumPreviewStatus } from '../store/schema/PremiumPreviewData';
import { updatePremiumPreviewData } from '../actions/premiumPreviewActions';
import { enableCritiquesOnBx } from '../utils/enableCritiquesOnBx';
import { getEditorBxClient } from '../utils/getEditorBxClient';
import { logTryItNowButtonClicked } from '../utils/telemetry/logTryItNowButtonClicked';

const styles = require('./UpsellSkittle.scss');
const classNames = require('classnames');

const UpsellSkittle = observer(function UpsellSkittle(
    props: PluginUIProps<ProofingViewState, ProofingPlugin>
) {
    const {
        skittleViewStatus,
        premimumFlagsFound,
        premiumCategories,
        skittleCalloutStatus,
        premiumPreviewData,
        editorProofingId,
    } = props.viewState;
    const skittleElement = React.useRef<HTMLDivElement>();
    const focusTrapZone = React.useRef<FocusTrapZone>();
    const shouldShowSkittle: boolean = skittleViewStatus === 'Show';
    const isStatusActivated: boolean =
        premiumPreviewData.premiumPreviewStatus === PremiumPreviewStatus.Activated;
    const isStatusNew: boolean =
        premiumPreviewData.premiumPreviewStatus === PremiumPreviewStatus.New;
    const updateSkittleCallout = (skittleCalloutStatus: SkittleCalloutStatus) => {
        onUpdateSkittleCallout(props.viewState, skittleCalloutStatus);
    };

    // when previewActive is true, we should highlight the premium suggestions while
    // the user hover on the skittle. when it's false, we should turn off the higlights.
    const setPremiumPreviewActive = React.useCallback(
        (previewActive: boolean) => {
            if (!isStatusActivated) {
                const editorProofing = getEditorBxClient(
                    editorProofingId,
                    null /*userCulture*/,
                    null /*composeOperation */,
                    premiumPreviewData.premiumPreviewStatus,
                    null /* augloopSession */
                );
                editorProofing.setLightUpPremiumSquiggles(previewActive);
            }
        },
        [premiumPreviewData.premiumPreviewStatus]
    );

    // When premium preview is activated, BX callback keeps updating premimumFlagsFound data.
    // we should not show this number to the user, as they see the actual squiggles
    const premimumFlagsToDisplay = isStatusActivated ? null : premimumFlagsFound;

    const onSkittleClicked = React.useCallback(() => {
        logOnSkittleClicked(premiumPreviewData.premiumPreviewStatus);
        updateSkittleCallout(SkittleCalloutStatus.ShowCallout);
        onUpdatePermiumPreviewSource(props.viewState, 'skittle');
        if (isStatusNew) {
            setPremiumPreviewActive(true /* previewActive */);
        }
        focusTrapZone.current.focus();
    }, [premiumPreviewData.premiumPreviewStatus, props.viewState]);

    const onMouseLeave = React.useCallback(() => {
        if (isStatusNew) {
            setPremiumPreviewActive(false /* previewActive */);
        }
    }, [premiumPreviewData.premiumPreviewStatus]);

    const onTryItNowClick = React.useCallback(
        (numerOfDaysLeft: number, premiumSuggestionsCount: number) => {
            // update store with new data
            updatePremiumPreviewData(
                props.viewState,
                premiumPreviewData.premiumPreviewType,
                numerOfDaysLeft,
                true /*shouldUpdateOptionSettings */,
                premiumSuggestionsCount
            );
            // enable premium suggestions to show up
            enableCritiquesOnBx(
                editorProofingId,
                premiumPreviewData,
                NUMBER_OF_DAYS_FREE_TRIAL,
                NUMBER_OF_SUGGESTIONS
            );
            // hide callout
            updateSkittleCallout(SkittleCalloutStatus.Hide);
            // log data that 'try now' within premium preview clicked
            logTryItNowButtonClicked(
                premiumPreviewData.premiumPreviewSource,
                false /* hasPremiumPreviewRenderedAfterClickTryNow */
            );
        },
        []
    );

    const onDismiss = React.useCallback(() => {
        updateSkittleCallout(SkittleCalloutStatus.Hide);
    }, []);

    const ariaLabel = getAriaLabelForSkittle(premimumFlagsFound, premiumPreviewData);
    const calloutElement = isStatusNew ? (
        <UpsellPremiumPreviewBase
            onTryItNowClick={onTryItNowClick}
            premiumPreviewData={premiumPreviewData}
            onHoverPremiumPreview={setPremiumPreviewActive}
        />
    ) : (
        <UpsellSkittleCallout
            premiumCategories={premiumCategories}
            premimumFlagsFound={premimumFlagsFound}
            premiumPreviewData={premiumPreviewData}
        />
    );

    if (shouldShowSkittle) {
        return (
            <div className={styles.skittleContainer}>
                <div ref={skittleElement}>
                    <DefaultButton
                        aria-label={ariaLabel}
                        className={styles.skittleInfo}
                        onMouseEnter={onSkittleClicked}
                        onMouseLeave={onMouseLeave}
                        onClick={onSkittleClicked}>
                        <span
                            className={classNames([
                                styles.skittleCount,
                                premimumFlagsFound == 0 && styles.hideCount,
                            ])}>
                            {premimumFlagsToDisplay}
                        </span>
                        <Icon
                            iconName={ControlIcons.Diamond}
                            className={styles.skittleDiamonIcon}
                        />
                    </DefaultButton>
                </div>
                <Callout
                    hidden={skittleCalloutStatus !== SkittleCalloutStatus.ShowCallout}
                    className={styles.reservationStatusCallout}
                    gapSpace={3}
                    onDismiss={onDismiss}
                    directionalHint={DirectionalHint.topRightEdge}
                    isBeakVisible={false}
                    setInitialFocus={true}
                    target={skittleElement.current}>
                    <FocusTrapZone
                        ref={component => (focusTrapZone.current = component)}
                        forceFocusInsideTrap={false}
                        isClickableOutsideFocusTrap={true}>
                        {calloutElement}
                    </FocusTrapZone>
                </Callout>
                {skittleCalloutStatus === SkittleCalloutStatus.ShowDialog && (
                    <DialogPremiumPreviewSkittle
                        onDismiss={onDismiss}
                        onTryItNowClick={onTryItNowClick}
                        premiumPreviewData={premiumPreviewData}
                    />
                )}
            </div>
        );
    }
    return null;
});

export { UpsellSkittle };
